import { RejectionReasonService } from './../../../../services/rejection-reason/rejection-reason.service';
import { Component, computed, inject, LOCALE_ID, signal, OnInit, DestroyRef } from '@angular/core';
import { Tables } from '../../../../shared/ui/shared-tables/tables/tables';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import { FilterItemSearch } from '../../../../shared/ui/shared-filters/filter-item-search/filter-item-search';
import { FilterDateComponent } from '../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterSortingComponent } from '../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';
import { finalize } from 'rxjs';
import { Loading } from '../../../../utils/loading/loading';
import { Error } from '../../../public-user-component/auth/shared/error/error';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { Router } from '@angular/router';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';

@Component({
  selector: 'app-rejection-reason-list',
  imports: [
    Tables,
    Pagination,
    FilterItemSearch,
    FilterDateComponent,
    FilterSortingComponent,
    SubmitButton,
    Loading,
    Error,
    ConfirmationDialog,
    SuccessfulNotification,
  ],
  templateUrl: './rejection-reason-list.html',
  styleUrl: './rejection-reason-list.scss',
})
export class RejectionReasonList implements OnInit {
  private locale = inject(LOCALE_ID);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private rejectionReasonService = inject(RejectionReasonService);

  ngOnInit() {
    this.loadRejectionReasons();
  }

  showSuccessNotification = signal(false);
  deleteErrorMessage = signal('');
  fromDateLabel = $localize`:@@adminCategoriesCreatedOnFrom:Created On (From)`;
  toDateLabel = $localize`:@@adminCategoriesCreatedOnTo:Created On (To)`;
  lastUpdatedOnFromLabel = $localize`:@@rejectionReasonLastUpdatedOnFrom:Last Updated On (From)`;
  lastUpdatedOnToLabel = $localize`:@@rejectionReasonLastUpdatedOnTo:Last Updated On (To)`;
  reasonNameLabel = $localize`:@@reasonTitle:Reason Name`;
  reasonNamePlaceholder = $localize`:@@rejectionReasonNamePlaceholder:Search by reason name`;

  deleteTitle = $localize`:@@deleteRejectionReasonTitle:Delete Rejection Reason`;
  deleteDescription = $localize`:@@deleteRejectionReasonDescription:Are you sure you want to delete this rejection reason?`;
  confirmButton = signal($localize`:@@delete:Delete`);
  Loading = $localize`:@@loading:Loading...`;
  cancelButton = $localize`:@@cancelBtn:Cancel`;
  showDeleteDialog = signal(false);
  selectedReasonId = signal<number | null>(null);
  selectedReasonTitle = signal('');

  onCancelDelete() {
    this.showDeleteDialog.set(false);
    this.deleteErrorMessage.set('');
  }

  onOrderByChange(value: string) {
    this.orderBy.set(value as 'TITLE' | 'CREATION_DATE' | 'LAST_MODIFICATION_DATE');

    this.currentPage.set(1);
    this.loadRejectionReasons();
  }

  private extractBackendError(error: any): string {
    const body = error?.error ?? error;

    const message = this.isArabic()
      ? (body?.messageAr ?? error?.messageAr ?? body?.message)
      : (body?.messageEn ?? error?.messageEn ?? body?.message);

    if (message) return message;

    if (typeof body === 'string' && body.trim()) return body;

    return this.isArabic()
      ? 'حدث خطأ أثناء الحذف، حاول مرة أخرى'
      : 'Something went wrong while deleting. Please try again.';
  }

  onConfirmDelete() {
    const reasonId = this.selectedReasonId();

    if (reasonId === null) {
      return;
    }

    this.confirmButton.set(this.Loading);

    const sub = this.rejectionReasonService.deleteRejectionReason(reasonId).subscribe({
      next: () => {
        this.confirmButton.set($localize`:@@delete:Delete`);

        this.rows.update((rows) =>
          rows
            .filter((row) => row['id'] !== reasonId)
            .map((row, index) => ({
              ...row,
              rowNumber: index + 1,
            })),
        );
        this.totalItems.update((total) => Math.max(0, total - 1));

        this.showDeleteDialog.set(false);
        this.deleteErrorMessage.set('');

        this.showSuccessNotification.set(true);

        setTimeout(() => {
          this.showSuccessNotification.set(false);
        }, 3000);
      },
      error: (error: any) => {
        this.confirmButton.set($localize`:@@delete:Delete`);
        this.deleteErrorMessage.set(this.extractBackendError(error));
      },
    });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  deleteSuccessMessage = computed(() => {
    const reasonTitle = this.selectedReasonTitle();

    if (!reasonTitle) {
      return '';
    }

    return this.isArabic()
      ? `تم حذف سبب الرفض '${reasonTitle}' بنجاح`
      : `Rejection reason '${reasonTitle}' deleted successfully`;
  });

  onActionTrigger(event: { event: Event; actionId: number; entity: any }) {
    console.log('ACTION:', event);
    if (event.actionId === 1) {
      this.router.navigate(['/admin/rejection-reasons/edit-reason', event.entity.id]);
      return;
    }

    if (event.actionId === 2) {
      this.selectedReasonId.set(event.entity.id);
      this.selectedReasonTitle.set(event.entity.title);
      this.deleteErrorMessage.set('');
      this.showDeleteDialog.set(true);
    }
  }

  lastUpdatedOnFrom = signal<string | null>(null);
  lastUpdatedOnTo = signal<string | null>(null);
  fromDate = signal<string | null>(null);
  toDate = signal<string | null>(null);

  isArabic = computed(() => this.locale.startsWith('ar'));

  searchTerm = signal('');
  orderBy = signal<'TITLE' | 'CREATION_DATE' | 'LAST_MODIFICATION_DATE'>('CREATION_DATE');
  filters = signal({
    orderDir: 'DESC' as 'ASC' | 'DESC',
  });
  sortOptions = signal([
    {
      value: 'TITLE',
      labelEn: 'Title',
      labelAr: 'العنوان',
    },
    {
      value: 'CREATION_DATE',
      labelEn: 'Created Date',
      labelAr: 'تاريخ الإنشاء',
    },
    {
      value: 'LAST_MODIFICATION_DATE',
      labelEn: 'Last Modified Date',
      labelAr: 'آخر تعديل',
    },
  ]);
  loading = signal(false);
  errorGettingRejectionReasons = signal<string | null>(null);
  headerItems = computed(() => [
    { key: 'rowNumber', label: '#' },
    {
      key: 'title',
      label: this.isArabic() ? 'العنوان' : 'Title',
    },
    {
      key: 'type',
      label: this.isArabic() ? 'النوع' : 'Type',
      type: 'status',
    },
    {
      key: 'createdOnBy',
      label: this.isArabic() ? 'تاريخ الإنشاء / بواسطة' : 'Created On / By',
    },
    {
      key: 'lastModifiedOnBy',
      label: this.isArabic() ? 'آخر تعديل / بواسطة' : 'Last Modified On / By',
    },
    {
      key: 'actions',
      label: '',
      type: 'actions',
    },
  ]);
  rows = signal<Record<string, any>[]>([]);
  emptyStateMessage = computed(() => {
    if (this.searchTerm().trim()) {
      return this.isArabic() ? 'لا توجد أسباب تطابق بحثك' : 'No reasons match your search';
    }

    const hasFilters =
      this.fromDate() !== null ||
      this.toDate() !== null ||
      this.lastUpdatedOnFrom() !== null ||
      this.lastUpdatedOnTo() !== null;

    if (hasFilters) {
      return this.isArabic() ? 'لا توجد أسباب تطابق الفلاتر المحددة' : 'No reasons match your selected filters';
    }

    return this.isArabic() ? 'لا توجد أسباب رفض' : 'No rejection reasons found';
  });

  loadRejectionReasons() {
    this.loading.set(true);
    this.errorGettingRejectionReasons.set(null);
    const subscription = this.rejectionReasonService
      .getAllRejectionReason(this.isArabic() ? 'ar' : 'en', {
        pageNum: this.currentPage() - 1,
        pageSize: this.itemsPerPage(),
        orderBy: this.orderBy(),
        orderDir: this.filters().orderDir,
        quickSearch: this.searchTerm() || undefined,
        createdOnFrom: this.fromDate() || undefined,
        createdOnTo: this.toDate() || undefined,
        lastModifiedOnFrom: this.lastUpdatedOnFrom() || undefined,
        lastModifiedOnTo: this.lastUpdatedOnTo() || undefined,
      })
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe({
        next: (res) => {
          const list = res.list.map((item, index) => ({
            ...item,
            rowNumber: index + 1,
            title: this.isArabic() ? item.titleAr : item.titleEn,
            createdBy: item.createdBy?.fullName ?? '-',
            lastModifiedBy: item.lastModifiedBy?.fullName ?? '-',
            type: {
              titleEn: item.isEditable ? 'Custom' : 'System-defined',
              titleAr: item.isEditable ? 'مخصص' : 'معرّف من النظام',
            },
          }));

          this.rows.set(list);
          this.totalItems.set(res.total);
        },
        error: (error) => {
          this.errorGettingRejectionReasons.set(
            this.isArabic()
              ? error.messageAr || 'حدث خطأ أثناء تحميل أسباب الرفض'
              : error.messageEn || 'Error loading rejection reasons.',
          );
        },
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  actions = signal([
    {
      id: 1,
      titleEn: 'Edit Reason',
      titleAr: 'تعديل السبب',
    },
    {
      id: 2,
      titleEn: 'Delete Reason',
      titleAr: 'حذف السبب',
    },
  ]);
  currentPage = signal(1);
  itemsPerPage = signal(10);

  totalItems = signal(0);

  totalPages = computed(() => Math.ceil(this.totalItems() / this.itemsPerPage()));

  startItem = computed(() => (this.totalItems() === 0 ? 0 : (this.currentPage() - 1) * this.itemsPerPage() + 1));

  endItem = computed(() => Math.min(this.currentPage() * this.itemsPerPage(), this.totalItems()));

  pageNumbers = computed(() => Array.from({ length: this.totalPages() }, (_, index) => index + 1));

  onApplyFilters() {
    this.currentPage.set(1);
    this.loadRejectionReasons();
  }

  onResetFilters() {
    this.searchTerm.set('');
    this.fromDate.set(null);
    this.toDate.set(null);
    this.lastUpdatedOnFrom.set(null);
    this.lastUpdatedOnTo.set(null);

    this.orderBy.set('CREATION_DATE');

    this.filters.set({
      orderDir: 'DESC',
    });

    this.currentPage.set(1);

    this.loadRejectionReasons();
  }

  onGoToPage(page: number) {
    this.currentPage.set(page);
    this.loadRejectionReasons();
  }

  onNextPage() {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((page) => page + 1);
      this.loadRejectionReasons();
    }
  }

  onPreviousPage() {
    if (this.currentPage() > 1) {
      this.currentPage.update((page) => page - 1);
      this.loadRejectionReasons();
    }
  }

  toggleSortOrder() {
    this.filters.update((filters) => ({
      ...filters,
      orderDir: filters.orderDir === 'ASC' ? 'DESC' : 'ASC',
    }));

    this.currentPage.set(1);
    this.loadRejectionReasons();
  }
}
